/**
 * DR Primary Filter Links
 * Standalone component for filtering collection by primary category.
 * Integrates with Dawn facet rendering to avoid full-section DOM swaps.
 */
(() => {
  const WRAPPER_SELECTOR = '[data-dr-component="primaryFilterLinks"]';
  const PILLS_SELECTOR = '.dr-FilterPills';
  const PILL_SELECTOR = '.dr-FilterPill';
  const SHOW_ALL_SELECTOR = '.dr-FilterPill[data-show-all="true"]';
  const LIVE_COUNT_SELECTOR = '[data-live-count]';
  const GRID_CONTAINER_SELECTOR = '#ProductGridContainer';
  const RENDER_FALLBACK_TIMEOUT_MS = 5000;
  let isRenderPending = false;
  let renderFallbackTimer = null;

  function getFallbackCount(wrapper) {
    const fallback = parseInt(wrapper.dataset.totalCount || '', 10);
    return Number.isFinite(fallback) ? fallback : null;
  }

  function syncWrapperLiveCount(wrapper) {
    const target = wrapper.querySelector(LIVE_COUNT_SELECTOR);
    if (!target) return;

    const fallbackCount = getFallbackCount(wrapper);
    const count = fallbackCount;
    if (!Number.isFinite(count)) return;

    target.textContent = `(${count})`;
  }

  function syncAllLiveCounts() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      syncWrapperLiveCount(wrapper);
    });
  }

  function syncWrapperState(wrapper) {
    const param = wrapper.dataset.param;
    if (!param) return;

    const currentParams = new URLSearchParams(window.location.search);
    const selectedValue = currentParams.get(param);

    wrapper.querySelectorAll(PILL_SELECTOR).forEach((pill) => {
      const isShowAll = pill.matches(SHOW_ALL_SELECTOR);
      const isPressed = isShowAll ? !selectedValue : selectedValue === pill.dataset.value;
      pill.setAttribute('aria-pressed', String(isPressed));
    });
  }

  function syncAllWrapperStates() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      syncWrapperState(wrapper);
    });
  }

  function scheduleSync() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncAllWrapperStates();
        syncAllLiveCounts();
      });
    });
  }

  function renderViaDawnFacets(targetUrl) {
    const searchParams = targetUrl.search.startsWith('?')
      ? targetUrl.search.slice(1)
      : targetUrl.search;

    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      try {
        FacetFiltersForm.renderPage(searchParams, null, true);
        return true;
      } catch (err) {
        console.error('DR primary filter: FacetFiltersForm render failed.', err);
        return false;
      }
    }

    return false;
  }

  function setLoadingState(isLoading) {
    const gridContainer = document.querySelector(GRID_CONTAINER_SELECTOR);
    const collectionRoot = gridContainer?.querySelector('.collection');
    if (collectionRoot) collectionRoot.classList.toggle('loading', isLoading);
  }

  function clearRenderFallbackTimer() {
    if (!renderFallbackTimer) return;
    clearTimeout(renderFallbackTimer);
    renderFallbackTimer = null;
  }

  function beginPendingRender() {
    isRenderPending = true;
    setLoadingState(true);
    clearRenderFallbackTimer();
    renderFallbackTimer = setTimeout(() => {
      isRenderPending = false;
      setLoadingState(false);
    }, RENDER_FALLBACK_TIMEOUT_MS);
  }

  function completePendingRender() {
    if (!isRenderPending) return;
    isRenderPending = false;
    clearRenderFallbackTimer();
    setLoadingState(false);
  }

  function setExclusivePillState(wrapper, activePill) {
    const showAllPill = wrapper.querySelector(SHOW_ALL_SELECTOR);

    wrapper.querySelectorAll(PILL_SELECTOR).forEach((pill) => {
      const isPressed = activePill ? pill === activePill : pill === showAllPill;
      pill.setAttribute('aria-pressed', String(isPressed));
    });
  }

  function buildExclusiveUrl(wrapper, pill, isActive) {
    const param = wrapper.dataset.param;
    if (!param) return null;

    const nextUrl = new URL(window.location.href);
    // Exclusive: drop all existing values; filter change restarts on page 1.
    nextUrl.searchParams.delete(param);
    nextUrl.searchParams.delete('page');

    const isShowAll = pill.matches(SHOW_ALL_SELECTOR);
    if (!isShowAll && !isActive && pill.dataset.value) {
      nextUrl.searchParams.set(param, pill.dataset.value);
    }
    // "All" or clicking the active pill leaves the param absent.
    return nextUrl;
  }

  function handleToggle(pill, wrapper) {
    const isActive = pill.getAttribute('aria-pressed') === 'true';
    const targetUrl = buildExclusiveUrl(wrapper, pill, isActive);
    if (!targetUrl) return;

    if (targetUrl.toString() === window.location.href) {
      setExclusivePillState(wrapper, pill.matches(SHOW_ALL_SELECTOR) ? pill : null);
      scheduleSync();
      return;
    }

    beginPendingRender();

    // Optimistic state for snappy feedback; authoritative state is synced after render/popstate.
    setExclusivePillState(wrapper, isActive ? null : pill);
    scheduleSync();

    const rendered = renderViaDawnFacets(targetUrl);
    if (!rendered) {
      completePendingRender();
      window.location.href = targetUrl.toString();
      return;
    }

    // Keep custom pills in sync after Dawn replaces facets/grid fragments.
    scheduleSync();
  }

  // Delegated click handler to survive partial DOM updates.
  document.addEventListener('click', (e) => {
    const pill = e.target.closest(PILL_SELECTOR);
    if (!pill) return;

    const wrapper = pill.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;

    e.preventDefault();
    handleToggle(pill, wrapper);
  });

  // Back/forward support.
  window.addEventListener('popstate', () => {
    scheduleSync();
  });

  // Keep pills synced when non-primary facets/sort trigger AJAX updates.
  const observerTarget = document.querySelector(GRID_CONTAINER_SELECTOR);
  if (observerTarget) {
    const observer = new MutationObserver(() => {
      scheduleSync();
      completePendingRender();
    });
    observer.observe(observerTarget, { childList: true, subtree: false });
  }

  scheduleSync();
})();
